import type { TypedDocumentNode } from '@apollo/client';
import { gql } from '@apollo/client';
import type { CSSProperties } from 'react';
import { FormattedMessage, FormattedNumber } from 'react-intl';
import styled from 'styled-components';

import {
  Horizontal,
  SBHorizontal,
  Vertical,
} from '@sorare/core/src/atoms/layout/flex';
import { CircularProgress } from '@sorare/core/src/atoms/loader/CircularProgress';
import { LoadingIndicator } from '@sorare/core/src/atoms/loader/LoadingIndicator';
import { LabelL, LabelS } from '@sorare/core/src/atoms/typography';
import { sportLogos } from '@sorare/core/src/components/SportLogo';
import { useSportContext } from '@sorare/core/src/contexts/sport';
import { COMPETITION_COLORS } from '@sorare/core/src/lib/colors';
import { glossary } from '@sorare/core/src/lib/glossary';

import type { LeaguePageHeader_competitionCardCollections } from './__generated__/index.graphql';

const Root = styled(Vertical)`
  padding: var(--quadruple-unit) var(--double-unit);
`;
const StyledCircularProgress = styled(CircularProgress)`
  width: 12px;
  height: 12px;
`;

const SportLogoWrapper = styled.div`
  & * {
    height: 64px;
  }
`;

const CompetitionLogo = styled.img`
  height: 64px;
  object-fit: contain;
`;
type Props = {
  competitionCardCollections: Nullable<LeaguePageHeader_competitionCardCollections>;
  loading: boolean;
};

export const LeaguePageHeader = ({
  competitionCardCollections,
  loading,
}: Props) => {
  const { sport } = useSportContext();
  const { competition, myStats } = competitionCardCollections || {};
  const { ownedCardsCount, totalCardsCount } = myStats ?? {
    ownedCardsCount: 0,
    totalCardsCount: 1,
  };
  return (
    <Root center gap={3}>
      {loading ? (
        <div style={{ height: 64 }}>
          <LoadingIndicator small />
        </div>
      ) : (
        <>
          {competition?.pictureUrl ? (
            <CompetitionLogo
              src={competition?.pictureUrl}
              alt={competition?.displayName}
            />
          ) : (
            <SportLogoWrapper>{sportLogos[sport]}</SportLogoWrapper>
          )}
        </>
      )}
      <SBHorizontal gap={4}>
        <Vertical center>
          <LabelS color="var(--c-nd-500)">
            <FormattedMessage
              id="LeaguePageHeader.completion"
              defaultMessage="Completion"
            />
          </LabelS>
          <Horizontal>
            <StyledCircularProgress
              progress={ownedCardsCount}
              maxProgress={totalCardsCount}
              style={
                {
                  '--circular-progress-stroke':
                    COMPETITION_COLORS[competition?.slug || ''],
                } as CSSProperties
              }
            />
            <LabelL bold>
              <FormattedNumber
                value={ownedCardsCount / totalCardsCount}
                maximumFractionDigits={0}
                style="percent"
              />
            </LabelL>
          </Horizontal>
        </Vertical>
        <Vertical center>
          <LabelS color="var(--c-nd-500)">
            <FormattedMessage {...glossary.cards} />
          </LabelS>
          <Horizontal gap={0.5}>
            <LabelL bold>{ownedCardsCount}</LabelL>/
            <LabelL bold color="var(--c-nd-500)">
              {totalCardsCount}
            </LabelL>
          </Horizontal>
        </Vertical>
      </SBHorizontal>
    </Root>
  );
};

LeaguePageHeader.fragments = {
  competitionCardCollections: gql`
    fragment LeaguePageHeader_competitionCardCollections on CompetitionCardCollections {
      competition {
        slug
        pictureUrl
        displayName
      }
      myStats {
        ownedCardsCount
        totalCardsCount
      }
    }
  ` as TypedDocumentNode<LeaguePageHeader_competitionCardCollections>,
};
